import type { AccountView, BundleView, CardView, DataFlag, LedgerEntry, MandateView, OccurrenceView, Receipt, ReceiptStep, SignedStep } from "../api/types.js";
import { accountNames, byAccountRank } from "./home-model.js";
import { clock, dateTime, dayHeading, formatMoney, lastDigits, proxyTail, sgDay, signedMoney, sumMoney } from "../lib/format.js";
import type { IconId } from "../lib/icon.js";
import { doneOf, nothingWent } from "./card-end.js";
import { describeStep, shownAs } from "./describe.js";
import { splitLine } from "./mandates-model.js";
import { mandateTitle, paymentAmount, ruleIcon, ruleShort } from "./schedule-words.js";

/**
 * 11 Activity (UX spec v3 §6.13): the statement as a list grouped by day.
 *
 * Three sources, each for what only it knows:
 *  - the LEDGER (`GET /v1/ledger/entries`) — what posted, when, on which account,
 *    and what LEFT (the day total counts debits that posted; a reservation for a
 *    held step has not left, so it is never summed);
 *  - each bundle's SIGNED PAYLOAD (`GET /v1/bundles/{id}`) — what an operation is
 *    called, in the words the customer signed (describe.ts, as on the card);
 *  - each bundle's RECEIPT (`GET /v1/gateway/bundles/{id}`) — which step ran, which
 *    waits, which did not.
 * A bundle is ONE entry (ask d: `bundle_id` on every entry it posted).
 *
 * DCTA-166 (Umakhan + Claude, 10 Oct — supersedes "a no is also a record" for the customer's history): the history is
 * what the customer SIGNED. A bundle is an entry only when its receipt carries the bank's `signed_at` (set when the
 * gateway verified the signature), and it lists only its signed steps, each with its outcome — a signed step that did
 * not run still belongs. Refused drafts, cards left unsigned or abandoned, questions, freezes, firewall refusals and
 * scam-check exits before signing are never rows: they stay in the bank's audit trail (judge mode). Nothing here reads
 * `GET /v1/drafts/refused`.
 *
 * The only arithmetic is a sum of the ledger's own debit strings, in integer minor
 * units (format.ts `sumMoney`).
 */
export interface ActivityInput {
  readonly entries: readonly LedgerEntry[];
  readonly accounts: readonly AccountView[];
  readonly bundles: ReadonlyMap<string, { readonly view: BundleView; readonly receipt: Receipt }>;
  /** Names a card's own lines (DCTA-77 ask y) by the card, never by its account id. */
  readonly cards?: readonly CardView[];
  readonly now: number;
  /** DCTA-209 (F57): route 1 — every schedule: what titles the payments they made, and the day each was set up. */
  readonly mandates?: readonly MandateView[];
  /** DCTA-209 (F57): route 2's skipped (or claimed) payments, read for each schedule whose `payments_skipped` is above 0. */
  readonly skipped?: ReadonlyMap<string, readonly OccurrenceView[]>;
}

/** Money that posted out of one account: what a day total and a filtered row sum. */
interface Debit {
  readonly account: string;
  readonly amount: string;
  readonly currency: string;
}

export interface OpRow {
  /** `${bundle_id}:${step_id}` — the receipt it opens. */
  readonly key: string;
  readonly ic: IconId;
  readonly title: string;
  readonly sub: string;
  readonly amount: string;
  /** "Held until 25 Sep 09:00", "Cancelled", "Not sent" — the `.st` part of the sub-line. */
  readonly status?: string;
  readonly muted: boolean;
  readonly accounts: readonly string[];
}

export interface BundleRow {
  readonly kind: "bundle";
  readonly id: string;
  readonly title: string;
  readonly sub: string;
  readonly amount: string;
  readonly ops: readonly OpRow[];
  readonly debits: readonly Debit[];
  readonly at: string;
}

/** A bundle of one operation is that operation: nothing to expand. */
export interface SingleRow {
  readonly kind: "single";
  readonly id: string;
  readonly op: OpRow;
  readonly sub: string;
  readonly amount: string;
  readonly debits: readonly Debit[];
  readonly at: string;
}

/** A ledger line the assistant did not make (a salary, a card payment, a reservation). */
export interface LineRow {
  readonly kind: "line";
  /** The ledger_ref — the receipt it opens. */
  readonly key: string;
  readonly initial: string;
  readonly title: string;
  readonly sub: string;
  readonly amount: string;
  readonly credit: boolean;
  readonly muted: boolean;
  readonly status?: string;
  readonly accounts: readonly string[];
  readonly debits: readonly Debit[];
  readonly at: string;
}

/**
 * DCTA-209 (F60, 30a): a payment a schedule made — ONE row on its own day, keyed `occ:{mandate_id}:{occurrence}`, never folded
 * into a bundle (every payment of a schedule carries its own `occ_` key, or the signing bundle's id). Titled as its card
 * named the step; "{HH:MM} · Scheduled · every month". A skipped one is muted with no minus, the bank's line after
 * "Scheduled ·" (`st` in bold, then `rest`). It opens its receipt (30b, 30c).
 */
export interface OccurrenceFeedRow {
  readonly kind: "occurrence";
  readonly key: string;
  readonly ic: IconId;
  readonly title: string;
  readonly sub: string;
  readonly st?: string;
  readonly rest?: string;
  readonly amount: string;
  readonly muted: boolean;
  readonly accounts: readonly string[];
  readonly debits: readonly Debit[];
  readonly at: string;
}

/** DCTA-209 (F57, 30e): the day a schedule was signed — one muted row, "{HH:MM} · Set up · every month"; it opens the schedule. */
export interface SetupRow {
  readonly kind: "setup";
  /** `mandate:{mandate_id}`. */
  readonly key: string;
  readonly ic: IconId;
  readonly title: string;
  readonly sub: string;
  readonly amount: string;
  readonly muted: true;
  readonly accounts: readonly string[];
  readonly debits: readonly Debit[];
  readonly at: string;
}

export type FeedRow = BundleRow | SingleRow | LineRow | OccurrenceFeedRow | SetupRow;

export interface Day {
  readonly key: string;
  readonly label: string;
  /** "−S$2,542.00": debits that posted that day; absent when nothing left. */
  readonly out?: string;
  readonly rows: readonly FeedRow[];
}

export interface Chip {
  readonly id: string;
  readonly label: string;
}

export interface ActivityFeed {
  readonly chips: readonly Chip[];
  readonly days: readonly Day[];
}

// ------------------------------------------------------------------ helpers
function money(step: SignedStep): { value: string; currency: string } | undefined {
  if (step.type === "securities_order") return step.cash_amount;
  if (step.type === "protective_action" || step.type === "payee_add") return undefined;
  return step.amount;
}

function accountsOf(step: SignedStep): string[] {
  switch (step.type) {
    case "funds_transfer":
      return [step.debit_account.account_id, ...(step.to_account === undefined ? [] : [step.to_account.account_id])];
    case "bill_payment":
      return [step.debit_account.account_id];
    case "securities_order":
      return [step.settlement_account.account_id];
    case "protective_action":
      return [step.target.account_id];
    case "payee_add":
      return [];
  }
}

/**
 * The feed's sub-line: who, and from where — shorter than the card's (the row wraps, never clips). DCTA-143: a string
 * the bank flagged is its inert form (`shownAs`).
 */
function feedSub(step: SignedStep, flags: readonly DataFlag[] = []): string {
  const shown = (field: string, value: string): string => shownAs(flags, step.step_id, field, value);
  switch (step.type) {
    case "bill_payment":
      return `from ${shown("debit_account/display_name", step.debit_account.display_name)}`;
    case "funds_transfer": {
      const from = shown("debit_account/display_name", step.debit_account.display_name);
      if (step.payee !== undefined) {
        return `${shown("payee/registered_name", step.payee.registered_name)} ${lastDigits(step.payee.masked_proxy)} · from ${from}`;
      }
      return `${step.to_account === undefined ? "" : `${lastDigits(step.to_account.masked_number)} · `}from ${from}`;
    }
    case "securities_order":
      return `from ${shown("settlement_account/display_name", step.settlement_account.display_name)}`;
    case "protective_action":
      return `${shown("target/display_name", step.target.display_name)} ${lastDigits(step.target.masked_number)}`;
    case "payee_add":
      return `${shown("payee/registered_name", step.payee.registered_name)} ${proxyTail(step.payee.masked_proxy)}`;
  }
}

function statusOf(r: ReceiptStep | undefined, step: SignedStep): string | undefined {
  if (r === undefined) return "Not sent";
  if (r.state === "executed") return undefined;
  if (r.state === "held") {
    const until = r.hold_until ?? step.policy.hold_until;
    return until === undefined ? "Held" : `Held until ${dateTime(until)}`;
  }
  if (r.state.startsWith("cancelled")) return "Cancelled";
  return "Not sent";
}

function debitsOf(entries: readonly LedgerEntry[]): Debit[] {
  return entries
    .filter((e) => e.direction === "debit" && e.entry_type !== "reservation")
    .map((e) => ({ account: e.account_id, amount: e.amount, currency: e.currency }));
}

function total(debits: readonly Debit[]): string | undefined {
  if (debits.length === 0) return undefined;
  const currency = debits[0]?.currency ?? "SGD";
  // One currency per sum: the ledger read covers SGD deposit accounts; anything else is not added in.
  const same = debits.filter((d) => d.currency === currency).map((d) => d.amount);
  const sum = sumMoney(same);
  return /^0\.00$/.test(sum) ? undefined : `−${formatMoney(sum, currency)}`;
}

const newestFirst = (a: { at: string }, b: { at: string }): number => b.at.localeCompare(a.at);

/** A bundle the bank says was signed: its two reads and the time the gateway verified the signature. */
interface Signed {
  readonly view: BundleView;
  readonly receipt: Receipt;
  readonly signedAt: string;
}

/**
 * DCTA-166: only what the bank says the customer signed — a receipt with `signed_at`. A card never signed (left, timed
 * out, or cancelled at the scam check before signing) reads with none, whatever way its id reached this page.
 */
function signedOnly(bundles: ActivityInput["bundles"]): Map<string, Signed> {
  const out = new Map<string, Signed>();
  for (const [id, b] of bundles) if (b.receipt.signed_at !== undefined) out.set(id, { ...b, signedAt: b.receipt.signed_at });
  return out;
}

// ---------------------------------------------------------------- the model
export function activityModel(input: ActivityInput): ActivityFeed {
  const names = accountNames(input.accounts, input.cards ?? []);
  const signed = signedOnly(input.bundles);
  const rows: FeedRow[] = [];

  // DCTA-209 (F60, spec §13.6): a line a schedule's payment posted carries `mandate` and no `bundle_id` — it belongs to that
  // payment, never to a bundle; its schedule is route 1's, joined on `mandate_id`.
  const mandates = input.mandates ?? [];
  const payments = new Map<string, { readonly m: MandateView; readonly lines: LedgerEntry[] }>();
  // Entries by bundle; a bundle whose two reads are not both here (or not signed) falls back to its lines.
  const byBundle = new Map<string, LedgerEntry[]>();
  for (const e of input.entries) {
    const sched = scheduleOf(e, mandates);
    if (sched !== undefined) {
      const key = `occ:${sched.m.mandate_id}:${sched.seq}`;
      const p = payments.get(key) ?? { m: sched.m, lines: [] };
      p.lines.push(e);
      payments.set(key, p);
      continue;
    }
    if (e.bundle_id !== undefined && signed.has(e.bundle_id)) {
      const list = byBundle.get(e.bundle_id) ?? [];
      list.push(e);
      byBundle.set(e.bundle_id, list);
    } else {
      rows.push(lineRow(e, names));
    }
  }
  for (const [id, entries] of byBundle) {
    const b = signed.get(id);
    if (b !== undefined) rows.push(bundleRow(id, b, entries));
  }
  // DCTA-143 (§F.2 18f): a signed bundle that posted nothing — every step refused or stopped when it ran — has no ledger
  // line to be found by; when Activity holds its two reads (the success sheet's Receipt reveals it by id), it is an entry
  // all the same, with no lines. Only such a bundle: one whose steps ran has lines (outside this page, it stays out).
  for (const [id, b] of signed) {
    if (!byBundle.has(id) && nothingWent(doneOf(b.view.signing_payload.steps, b.receipt))) rows.push(bundleRow(id, b, []));
  }
  // DCTA-209 (F57, F60): each payment a schedule made, each one it skipped (route 2; never in the ledger), each set-up day.
  for (const [key, p] of payments) rows.push(paymentRow(key, p.m, p.lines));
  for (const m of mandates) {
    for (const o of input.skipped?.get(m.mandate_id) ?? []) if (o.state !== "executed") rows.push(skippedRow(m, o));
    rows.push(setupRow(m));
  }

  return {
    chips: [{ id: "all", label: "All" }, ...byAccountRank(input.accounts).map((a) => ({ id: a.account_id, label: a.display_name }))],
    days: toDays(rows, input.now, undefined),
  };
}

/** DCTA-209: the schedule a ledger line's payment came from — its `mandate`, joined to route 1 on `mandate_id`. */
function scheduleOf(e: LedgerEntry, mandates: readonly MandateView[]): { readonly m: MandateView; readonly seq: string } | undefined {
  const ref = e.mandate;
  if (ref === undefined) return undefined;
  const m = mandates.find((x) => x.mandate_id === ref.mandate_id);
  return m === undefined ? undefined : { m, seq: ref.occurrence };
}

const mandateAccounts = (m: MandateView): string[] => [m.from.account_id, ...(m.to.kind === "own_account" ? [m.to.id] : [])];

/** A schedule's amount with no sign (nothing moved): the payment's or the set amount, or a bill rule's cap; none for its skip. */
function quiet(m: MandateView, o?: OccurrenceView): string {
  const a = paymentAmount(m, o) ?? (o === undefined && m.amount.kind !== "fixed" ? m.amount.cap : undefined);
  return a === undefined ? "" : formatMoney(a.value, a.currency);
}

/** 30a: a payment that ran — its lines (a move to an own account posts two) as one row; what left is its debit. */
function paymentRow(key: string, m: MandateView, lines: readonly LedgerEntry[]): OccurrenceFeedRow {
  const debit = lines.find((l) => l.direction === "debit") ?? (lines[0] as LedgerEntry);
  return {
    kind: "occurrence",
    key,
    ic: ruleIcon(m.schedule),
    title: mandateTitle(m),
    sub: `${clock(debit.posted_at)} · Scheduled · ${ruleShort(m.schedule)}`,
    amount: signedMoney(debit.amount, debit.currency, debit.direction),
    muted: false,
    accounts: mandateAccounts(m),
    debits: debitsOf(lines),
    at: debit.posted_at,
  };
}

/** 30a: a payment the schedule skipped — on its due day at the signed time, muted, no minus, the bank's line verbatim. */
function skippedRow(m: MandateView, o: OccurrenceView): OccurrenceFeedRow {
  const { st, rest } = splitLine(o.line);
  return {
    kind: "occurrence",
    key: `occ:${m.mandate_id}:${o.seq}`,
    ic: ruleIcon(m.schedule),
    title: mandateTitle(m),
    sub: `${m.schedule.due_at_local} · Scheduled · `,
    st,
    rest,
    amount: quiet(m, o),
    muted: true,
    accounts: mandateAccounts(m),
    debits: [],
    at: dueInstant(o.due_on, m.schedule.due_at_local),
  };
}

/** The instant a payment fell due: its signed date at its signed local time, in Singapore (UTC+8, no daylight time). */
function dueInstant(day: string, local: string): string {
  const at = new Date(`${day}T${/^\d{2}:\d{2}$/.test(local) ? local : "09:00"}:00+08:00`);
  return Number.isNaN(at.getTime()) ? `${day}T01:00:00.000Z` : at.toISOString();
}

/** 30e: the day it was signed — one muted row, "Set up", never "Sent". */
function setupRow(m: MandateView): SetupRow {
  return {
    kind: "setup",
    key: `mandate:${m.mandate_id}`,
    ic: ruleIcon(m.schedule),
    title: mandateTitle(m),
    sub: `${clock(m.created_at)} · Set up · ${ruleShort(m.schedule)}`,
    amount: quiet(m),
    muted: true,
    accounts: mandateAccounts(m),
    debits: [],
    at: m.created_at,
  };
}

function lineRow(e: LedgerEntry, names: ReadonlyMap<string, string>): LineRow {
  const title = e.counterparty_name ?? e.reference ?? names.get(e.account_id) ?? "Bank";
  const reserved = e.entry_type === "reservation";
  const credit = e.direction === "credit";
  const figure = formatMoney(e.amount, e.currency);
  return {
    kind: "line",
    key: e.ledger_ref,
    initial: (/[\p{L}\p{N}]/u.exec(title)?.[0] ?? "·").toUpperCase(),
    title,
    sub: `${clock(e.posted_at)} · ${names.get(e.account_id) ?? lastDigits(e.account_id)}`,
    amount: reserved ? figure : `${credit ? "+" : "−"}${figure}`,
    credit: credit && !reserved,
    muted: reserved,
    ...(reserved ? { status: "Reserved" } : {}),
    accounts: [e.account_id],
    debits: debitsOf([e]),
    at: e.posted_at,
  };
}

/**
 * One signed bundle: its SIGNED steps, each with its outcome, at the time the bank verified the signature. A step the
 * policy refused before signing (`view.refused_steps`) was never signed, so it is not part of the entry (DCTA-166).
 */
function bundleRow(id: string, { view, receipt, signedAt: at }: Signed, entries: readonly LedgerEntry[]): BundleRow | SingleRow {
  const results = new Map(receipt.steps.map((r) => [r.step_id ?? r.index, r]));
  const steps = view.signing_payload.steps;
  // DCTA-143: the bank's flags ride with the bundle read — a flagged string is printed in its inert form here too.
  const flags = view.firewall?.data_flags ?? [];
  const ops: OpRow[] = steps.map((step) => {
    const r = results.get(step.step_id) ?? results.get(step.index);
    const d = describeStep(step, flags);
    const m = money(step);
    const status = statusOf(r, step);
    return {
      key: `${id}:${step.step_id}`,
      ic: d.ic,
      title: d.title,
      sub: feedSub(step, flags),
      amount: m === undefined ? "" : formatMoney(m.value, m.currency),
      ...(status === undefined ? {} : { status }),
      muted: status !== undefined,
      accounts: accountsOf(step),
    };
  });
  const debits = debitsOf(entries);
  const out = total(debits);
  const only = ops.length === 1 ? ops[0] : undefined;
  if (only !== undefined) {
    return {
      kind: "single",
      id,
      op: only,
      sub: `${clock(at)} · ${only.sub}`,
      amount: out ?? only.amount,
      debits,
      at,
    };
  }
  const ran = steps.filter((s) => (results.get(s.step_id) ?? results.get(s.index))?.state === "executed").length;
  const held = steps.filter((s) => (results.get(s.step_id) ?? results.get(s.index))?.state === "held").length;
  const notSent = ops.length - ran - held;
  // §F.2 18f (ask F19): nothing went through and nothing waits — "Not sent" over "{HH:MM} · signed once" (not "0 of 2 done").
  const nothing = nothingWent({ ran, held, of: ops.length });
  const parts = [`${clock(at)} · signed once`];
  if (held > 0) parts.push(`${String(held)} held`);
  if (notSent > 0 && !nothing) parts.push(`${String(notSent)} not sent`);
  return {
    kind: "bundle",
    id,
    title: nothing ? "Not sent" : ran === ops.length ? `${String(ops.length)} things in one go` : `${String(ran)} of ${String(ops.length)} done`,
    sub: parts.join(" · "),
    amount: out ?? "",
    ops,
    debits,
    at,
  };
}

function toDays(rows: readonly FeedRow[], now: number, account: string | undefined): Day[] {
  const days = new Map<string, FeedRow[]>();
  for (const row of [...rows].sort(newestFirst)) {
    const key = sgDay(row.at);
    const list = days.get(key) ?? [];
    list.push(row);
    days.set(key, list);
  }
  return [...days.entries()].map(([key, list]) => {
    const debits = list.flatMap((r) => r.debits).filter((d) => account === undefined || d.account === account);
    const out = total(debits);
    return { key, label: dayHeading((list[0] as FeedRow).at, now), ...(out === undefined ? {} : { out }), rows: list };
  });
}

/**
 * One account: the rows that touched it, a bundle keeping only its operations that
 * did. Day totals and a bundle's figure are what left THAT account.
 */
export function filterFeed(feed: ActivityFeed, account: string, now: number = Date.now()): ActivityFeed {
  if (account === "all") return feed;
  const kept: FeedRow[] = [];
  for (const day of feed.days) {
    for (const row of day.rows) {
      if (row.kind === "line") {
        if (row.accounts.includes(account)) kept.push(row);
        continue;
      }
      if (row.kind === "single") {
        if (row.op.accounts.includes(account)) kept.push(row);
        continue;
      }
      if (row.kind === "occurrence" || row.kind === "setup") {
        if (row.accounts.includes(account)) kept.push(row);
        continue;
      }
      const ops = row.ops.filter((o) => o.accounts.includes(account));
      if (ops.length === 0) continue;
      const debits = row.debits.filter((d) => d.account === account);
      kept.push({ ...row, ops, debits, amount: total(debits) ?? "" });
    }
  }
  // Day labels are re-derived with the same clock the feed was made with.
  const labels = new Map(feed.days.map((d) => [d.key, d.label]));
  return {
    chips: feed.chips,
    days: toDays(kept, now, account).map((d) => ({ ...d, label: labels.get(d.key) ?? d.label })),
  };
}
